import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useStore } from "utils/store";

const Container = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  padding: 20px;
  width: 280px;
  height: auto;
  background-color: rgba(0, 0, 0, 0.9);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  border-top-right-radius: 0;
  z-index: 9999;
`;

const OpenDevToolButtonContainer = styled.div`
  opacity: 0;
  &:hover {
    opacity: 1;
  }
  width: 100px;
  height: 42px;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 2;
  cursor: pointer;
`;

const OpenDevToolButton = styled.div`
  background-color: transparent;
  color: rgba(255, 255, 255, 0.6);
  padding: 0;
  font-family: var(--font-teko);
  box-shadow: none;
  border: none;
  position: absolute;
  top: 10;
  right: 30px;
`;

const IsReplayMessage = styled.div`
  position: fixed;
  top: 16px;
  right: 30px;
  background-color: transparent;
  color: rgba(255, 0, 0, 0.6);
  padding: 0;
  z-index: 2;
  font-family: var(--font-teko);
  box-shadow: none;
  opacity: 1;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: transparent;
  color: #fff;
  padding: 0;
  box-shadow: none;
  border: none;
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SectionHeader = styled.div`
  position: relative;
  color: transparent;
  font-family: var(--font-conthrax);
  box-shadow: none;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 1.07px;
  margin-bottom: 12px;
  background-image: linear-gradient(
    78deg,
    #0ad0ff -3%,
    #6931f9 48%,
    #e10eff 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Divider = styled.div`
  width: 100%;
  height: 0px;
  opacity: 0.5;
  border-top: solid 2px rgba(255, 255, 255, 0.3);
  margin: 12px 0;
`;

const textStyle = css`
  color: #fff;
  font-family: var(--font-teko);
  font-size: 18px;
`;

const Button = styled.button`
  ${textStyle}
  width: auto;
  height: 25px;
  padding: 5px 20px 0;
  font-weight: 500;
  line-height: 1.24;
  letter-spacing: 0.8px;
  border-radius: 17px;
  border: solid 1px #ff961b;
  background-color: #1a1a1a;
  text-transform: uppercase;
  color: #ff8100;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &:hover {
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.2;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 12px;
  position: relative;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  gap: 10px;
  position: relative;
`;

const Text = styled.div`
  ${textStyle}
  height: 25px;
  padding-top: 4px;
  margin-right: 4px;
  width: auto;
`;

function downloadObjectAsJson(exportObj, exportName) {
  var dataStr =
    "data:text/json;charset=utf-8," +
    encodeURIComponent(JSON.stringify(exportObj));
  var downloadAnchorNode = document.createElement("a");
  downloadAnchorNode.setAttribute("href", dataStr);
  downloadAnchorNode.setAttribute("download", exportName + ".json");
  document.body.appendChild(downloadAnchorNode); // required for firefox
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
}

const readFile = async (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file, "utf-8");
    reader.onload = () => {
      try {
        const { result } = reader;
        if (!result) reject();
        resolve(result as string);
      } catch (error) {
        reject();
      }
    };
  });
};

export const DevTools: React.FC = () => {
  // Dispatch actions on replayer mode

  const [isDevToolOpen, setIsDevToolOpen] = useState(false);
  const uploaderRef = useRef<HTMLInputElement>(null);
  const enableDebug = useStore(state => state.enableDebug);

  const downloadRecording = () => {
    const dataString = window.localStorage.getItem("BH:Data");
    if (dataString) {
      const dataObj = JSON.parse(dataString);
      downloadObjectAsJson(dataObj, "BH-rec-" + dataObj?.details.id);
    }
  };

  const importData = (fileRead: string) => {
    if (fileRead) {
      window.localStorage.setItem("BH:Data", fileRead);
      window.localStorage.setItem("BH:UseRecording", "true");
      window.location.reload();
    }
  };

  const handleInput = async ({ target }) => {
    const file = target.files?.[0];
    if (!file) return;

    let fileRead = (await readFile(file)) as string;
    if(fileRead.search('sessionId') < 0){
      const date = new Date();
      const data = JSON.parse(fileRead.replaceAll(/ObjectId|NumberInt|\(|\)/g, ''));
      const formatted = {
        _id: data._id,
        tournamentId: data.tournamentId,
        playerId: data.playerId,
        buyIn: 100,
        userId: "81d25e94-46ef-4666-97fe-3af785617632",
        gameId: "bh",
        configId: "bh-100",
        sessionId: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6Impla2llZmViMDMiLCJzdWIiOiI4MWQyNWU5NC00NmVmLTQ2NjYtOTdmZS0zYWY3ODU2MTc2MzIiLCJpYXQiOjE3Mzg1NjA0NTEsImV4cCI6MTczODY0Njg1MX0.shTvELW9Hrkv-8Cc0L7QQ0KbkcpikQd5_Oa4aT1FFuU",
        state: "UNFINISHED_GAME",
        createdAt: date.toUTCString(),
        updatedAt: date.toUTCString(),
        details: {
          id: data._id,
          tournamentSize: 0,
          tournamentId: data.tournamentId,
          hands: data.hands,
          jackpotDetails: data.jackpotDetails,
          summary: data.summary,
        }
      };
      fileRead = JSON.stringify(formatted);
    }

   importData(fileRead);
  };

  if(!enableDebug) return null;

  if (!isDevToolOpen)
    return (
      <>
        {window.localStorage.getItem("BH:UseRecording") === "true" && (
          <IsReplayMessage>Is Replay</IsReplayMessage>
        )}
        <OpenDevToolButtonContainer onClick={() => setIsDevToolOpen(true)}>
          <OpenDevToolButton>
            Devtool
          </OpenDevToolButton>
        </OpenDevToolButtonContainer>
      </>
    );

  return (
    <Container>
      <CloseButton
        type="button"
        onClick={() => {
          setIsDevToolOpen(false);
        }}
      >
        x
      </CloseButton>
      <SectionHeader>Export/Import Data</SectionHeader>
      <Divider />
      <Column>
        {window.localStorage.getItem("BH:UseRecording") !== "true" && (
          <Row>
            <Text>Export Current Data</Text>
            <Button
              type="button"
              onClick={downloadRecording}
              style={{ width: 140, marginRight: 4 }}
            >
              Export
            </Button>
          </Row>
        )}

        <input
          type="file"
          ref={uploaderRef}
          multiple={false}
          accept=".json,.gcrec"
          style={{ display: " none" }}
          onChange={handleInput}
        />
        <Row>
          <Text>Import Current Data</Text>
          <Button
            type="button"
            onClick={() => {
              if (!uploaderRef.current) return;
              uploaderRef.current.click();
            }}
          >
            Import
          </Button>
        </Row>

        {window.localStorage.getItem("BH:UseRecording") === "true" && (
          <Row>
            <Text>Clear Data</Text>
            <Button
              type="button"
              onClick={() => {
                window.localStorage.setItem("BH:UseRecording", "false");
                window.location.reload();
              }}
            >
              Clear Replay
            </Button>
          </Row>
        )}
      </Column>
      <Divider />
    </Container>
  );
};
